import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-context";

const UNSUBSCRIBE_ALL_SENTINEL = "__UNSUBSCRIBED_FROM_ALL__";

interface NotificationPreference {
  id: number;
  user: number;
  application: number | null;
  enabled_event_types: string[];
  enabled_stages: string[];
  enabled_stip_templates: string[];
  created_at: string;
  updated_at: string;
  lender_notification_options_available?: boolean;
}

const EVENT_TYPES = [
  { code: "STAGE_CHANGED", label: "Stage Changed" },
  { code: "APPLICATION_SIGNED", label: "Application Signed" },
  { code: "LENDER_STATUS_CHANGED", label: "Lender Status Changed" },
  { code: "STIP_UPLOADED", label: "Stipulation Uploaded" },
  { code: "STIP_STATUS_CHANGED", label: "Stipulation Status Changed" },
  { code: "MESSAGE_POSTED", label: "Message Posted (dealer posted on a deal)" },
  { code: "MESSAGE_REPLY", label: "Message Reply (internal replied on a deal)" },
];

interface StipTemplate {
  id: number;
  name: string;
  description: string;
  destination: "CUSTOMER" | "INTERNAL";
  is_active: boolean;
}

const STAGES = [
  { code: "CREATED", label: "Created" },
  { code: "IN_PROGRESS", label: "In Progress" },
  { code: "RECEIVED", label: "Received" },
  { code: "IN_REVIEW", label: "In Review" },
  { code: "SUBMITTED", label: "Submitted" },
  { code: "APPROVED", label: "Approved" },
  { code: "CONTRACTS_REQUESTED", label: "Contracts Requested" },
  { code: "CONTRACTS_OUT", label: "Contracts Out" },
  { code: "CONTRACTS_IN", label: "Contracts In" },
  { code: "FUNDED", label: "Funded" },
  { code: "DECLINED", label: "Declined" },
  { code: "DEAL_LOST", label: "Deal Lost" },
];

export function NotificationPreferencesPage() {
  const queryClient = useQueryClient();
  const { user, isLoading: authLoading } = useAuth();

  const hideLenderEventType =
    user?.role === "ORG_ADMIN" ||
    user?.role === "SALES" ||
    user?.role === "TERRITORY_SALES_MANAGER";

  const visibleEventTypes = useMemo(
    () =>
      hideLenderEventType
        ? EVENT_TYPES.filter((e) => e.code !== "LENDER_STATUS_CHANGED")
        : EVENT_TYPES,
    [hideLenderEventType]
  );

  const { data: preferences, isLoading: prefsLoading } = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: async () => {
      const { data } = await customInstance.get<NotificationPreference>("/api/notification-preferences/");
      return data;
    },
  });

  const { data: stipTemplates, isLoading: stipsLoading } = useQuery({
    queryKey: ["stipulation-templates"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ results: StipTemplate[] } | StipTemplate[]>(
        "/api/stipulation-templates/"
      );
      return Array.isArray(data) ? data : (data.results ?? []);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { enabled_event_types: string[]; enabled_stages: string[]; enabled_stip_templates: string[] }) => {
      await customInstance.put("/api/notification-preferences/update/", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
      toast.success("Notification preferences updated successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to update preferences");
    },
  });

  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedStips, setSelectedStips] = useState<string[]>([]);

  const [unsubscribeAllEventTypes, setUnsubscribeAllEventTypes] = useState(false);
  const [unsubscribeAllStages, setUnsubscribeAllStages] = useState(false);
  const [unsubscribeAllStips, setUnsubscribeAllStips] = useState(false);

  const unsubscribeAllEventTypesRef = useRef(false);
  const unsubscribeAllStagesRef = useRef(false);
  const unsubscribeAllStipsRef = useRef(false);

  useEffect(() => {
    unsubscribeAllEventTypesRef.current = unsubscribeAllEventTypes;
  }, [unsubscribeAllEventTypes]);
  useEffect(() => {
    unsubscribeAllStagesRef.current = unsubscribeAllStages;
  }, [unsubscribeAllStages]);
  useEffect(() => {
    unsubscribeAllStipsRef.current = unsubscribeAllStips;
  }, [unsubscribeAllStips]);

  useEffect(() => {
    if (preferences) {
      const eventTypes = preferences.enabled_event_types || [];
      const stages = preferences.enabled_stages || [];
      const stips = preferences.enabled_stip_templates || [];

      if (eventTypes.includes(UNSUBSCRIBE_ALL_SENTINEL)) {
        setUnsubscribeAllEventTypes(true);
        setSelectedEventTypes([]);
      } else {
        setUnsubscribeAllEventTypes(false);
        setSelectedEventTypes(eventTypes);
      }

      if (stages.includes(UNSUBSCRIBE_ALL_SENTINEL)) {
        setUnsubscribeAllStages(true);
        setSelectedStages([]);
      } else {
        setUnsubscribeAllStages(false);
        setSelectedStages(stages);
      }

      if (stips.includes(UNSUBSCRIBE_ALL_SENTINEL)) {
        setUnsubscribeAllStips(true);
        setSelectedStips([]);
      } else {
        setUnsubscribeAllStips(false);
        setSelectedStips(stips);
      }
    }
  }, [preferences]);

  const isEventTypeSubscribed = (eventTypeCode: string) => {
    if (unsubscribeAllEventTypes) return false;
    if (selectedEventTypes.length === 0) return true;
    return selectedEventTypes.includes(eventTypeCode);
  };

  const isStageSubscribed = (stageLabel: string) => {
    if (unsubscribeAllStages) return false;
    if (selectedStages.length === 0) return true;
    return selectedStages.includes(stageLabel);
  };

  const isStipSubscribed = (stipName: string) => {
    if (unsubscribeAllStips) return false;
    if (selectedStips.length === 0) return true;
    return selectedStips.includes(stipName);
  };

  const handleEventTypeToggle = (eventTypeCode: string) => {
    const wasUnsubAll = unsubscribeAllEventTypesRef.current;
    setUnsubscribeAllEventTypes(false);

    setSelectedEventTypes((prev) => {
      const allCodes = visibleEventTypes.map((e) => e.code);
      if (wasUnsubAll) {
        return [eventTypeCode];
      }
      const isAllSubscribed =
        prev.length === 0 ||
        (prev.length === allCodes.length && allCodes.every((code) => prev.includes(code)));

      if (isAllSubscribed) {
        return allCodes.filter((c) => c !== eventTypeCode);
      }
      if (prev.includes(eventTypeCode)) {
        const newList = prev.filter((e) => e !== eventTypeCode);
        if (newList.length === 0) {
          setUnsubscribeAllEventTypes(true);
          return [];
        }
        return newList;
      }
      const newList = [...prev, eventTypeCode];
      if (newList.length === allCodes.length && allCodes.every((code) => newList.includes(code))) {
        return [];
      }
      return newList;
    });
  };

  const handleStageToggle = (stageLabel: string) => {
    const wasUnsubAll = unsubscribeAllStagesRef.current;
    setUnsubscribeAllStages(false);

    setSelectedStages((prev) => {
      const allLabels = STAGES.map((s) => s.label);
      if (wasUnsubAll) {
        return [stageLabel];
      }
      const isAllSubscribed =
        prev.length === 0 ||
        (prev.length === allLabels.length && allLabels.every((label) => prev.includes(label)));

      if (isAllSubscribed) {
        return allLabels.filter((s) => s !== stageLabel);
      }
      if (prev.includes(stageLabel)) {
        const newList = prev.filter((s) => s !== stageLabel);
        if (newList.length === 0) {
          setUnsubscribeAllStages(true);
          return [];
        }
        return newList;
      }
      const newList = [...prev, stageLabel];
      if (newList.length === allLabels.length && allLabels.every((label) => newList.includes(label))) {
        return [];
      }
      return newList;
    });
  };

  const handleStipToggle = (stipName: string) => {
    const wasUnsubAll = unsubscribeAllStipsRef.current;
    setUnsubscribeAllStips(false);

    setSelectedStips((prev) => {
      if (!stipTemplates) return prev;
      const allNames = stipTemplates.map((t) => t.name);
      if (wasUnsubAll) {
        return [stipName];
      }
      const isAllSubscribed =
        prev.length === 0 ||
        (prev.length === allNames.length && allNames.every((name) => prev.includes(name)));

      if (isAllSubscribed) {
        return allNames.filter((s) => s !== stipName);
      }
      if (prev.includes(stipName)) {
        const newList = prev.filter((s) => s !== stipName);
        if (newList.length === 0) {
          setUnsubscribeAllStips(true);
          return [];
        }
        return newList;
      }
      const newList = [...prev, stipName];
      if (newList.length === allNames.length && allNames.every((name) => newList.includes(name))) {
        return [];
      }
      return newList;
    });
  };

  const handleSubscribeAllEventTypes = () => {
    setUnsubscribeAllEventTypes(false);
    setSelectedEventTypes([]);
  };

  const handleUnsubscribeAllEventTypes = () => {
    setUnsubscribeAllEventTypes(true);
    setSelectedEventTypes([]);
  };

  const handleSubscribeAllStages = () => {
    setUnsubscribeAllStages(false);
    setSelectedStages([]);
  };

  const handleUnsubscribeAllStages = () => {
    setUnsubscribeAllStages(true);
    setSelectedStages([]);
  };

  const handleSubscribeAllStips = () => {
    setUnsubscribeAllStips(false);
    setSelectedStips([]);
  };

  const handleUnsubscribeAllStips = () => {
    if (stipTemplates) {
      setUnsubscribeAllStips(true);
      setSelectedStips([]);
    }
  };

  const handleSave = () => {
    updateMutation.mutate({
      enabled_event_types: unsubscribeAllEventTypes
        ? [UNSUBSCRIBE_ALL_SENTINEL]
        : selectedEventTypes,
      enabled_stages: unsubscribeAllStages ? [UNSUBSCRIBE_ALL_SENTINEL] : selectedStages,
      enabled_stip_templates: unsubscribeAllStips ? [UNSUBSCRIBE_ALL_SENTINEL] : selectedStips,
    });
  };

  if (authLoading || prefsLoading || stipsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notification Preferences</h1>
        <p className="mt-2 text-sm text-gray-600">
          Choose which notifications you want to receive. Uncheck items to unsubscribe. If you uncheck every
          option in a section, you will not receive notifications for that section until you opt back in.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Event Type Notifications</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleUnsubscribeAllEventTypes}>
                Unsubscribe All
              </Button>
              <Button variant="outline" size="sm" onClick={handleSubscribeAllEventTypes}>
                Subscribe All
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Unchecking an event type unsubscribes you from notifications in that category (subject to the stage
            and stip filters below where applicable).
          </p>
          <p className="text-xs text-amber-800/90 dark:text-amber-200/90 mt-2 rounded-md border border-amber-200/80 bg-amber-50/80 dark:bg-amber-950/40 px-3 py-2">
            <strong>Internal team tags:</strong> If you are a super admin or account manager, email and in-app
            alerts still fire when a teammate <strong>tags you</strong> on an application&apos;s internal
            discussion (tag buttons on the deal page). That is separate from the event types above.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleEventTypes.map((eventType) => {
              const isSubscribed = isEventTypeSubscribed(eventType.code);
              return (
                <div key={eventType.code} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`event-${eventType.code}`}
                    checked={isSubscribed}
                    onChange={() => handleEventTypeToggle(eventType.code)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor={`event-${eventType.code}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {eventType.label}
                  </label>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Application Stage Notifications</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleUnsubscribeAllStages}>
                Unsubscribe All
              </Button>
              <Button variant="outline" size="sm" onClick={handleSubscribeAllStages}>
                Subscribe All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STAGES.map((stage) => {
              const isSubscribed = isStageSubscribed(stage.label);
              return (
                <div key={stage.code} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`stage-${stage.code}`}
                    checked={isSubscribed}
                    onChange={() => handleStageToggle(stage.label)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor={`stage-${stage.code}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {stage.label}
                  </label>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Stipulation Notifications</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleUnsubscribeAllStips}>
                Unsubscribe All
              </Button>
              <Button variant="outline" size="sm" onClick={handleSubscribeAllStips}>
                Subscribe All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {stipTemplates && stipTemplates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {stipTemplates.map((template) => {
                const isSubscribed = isStipSubscribed(template.name);
                return (
                  <div key={template.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`stip-${template.id}`}
                      checked={isSubscribed}
                      onChange={() => handleStipToggle(template.name)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor={`stip-${template.id}`}
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      {template.name}
                    </label>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No stipulation templates available.</p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? "Saving..." : "Save Preferences"}
        </Button>
      </div>
    </div>
  );
}
